import { Button, Dropdown, TextInput } from '@carbon/react';
import { Add, TrashCan } from '@carbon/react/icons';
import { useScenarioForm } from './contexts';
import type { PriceTier, TaxBasis, TaxEntry, TaxType } from './types';
import { TAX_TYPE_LABELS, TAX_TYPE_NOTES, VARIABLE_ELIGIBLE } from './utils';
import styles from './tax-rates-table.module.scss';

let nextTierClientId = -1000;
const assignClientId = (): number => nextTierClientId--;

interface TaxRatesTableProps {
  areaIndex: number;
}

const BASIS_OPTIONS: { id: TaxBasis; label: string }[] = [
  { id: 'percentage', label: '% Rev' },
  { id: 'per_well', label: '$/Well' },
  { id: 'variable', label: 'Variable' },
];

const STANDARD_BASIS = BASIS_OPTIONS.filter((b) => b.id !== 'variable');

const TAX_TYPE_PRICE_UNIT: Record<string, string> = {
  severance_oil: '$/BBL',
  severance_gas: '$/MCF',
};

/**
 * Tax rates table — 6 fixed rows (one per tax type), each with basis selection
 * and rate input. Variable basis shows nested tier editor.
 * @param {TaxRatesTableProps} props - The component props
 * @return {JSX.Element} The tax rates table
 */
export function TaxRatesTable({ areaIndex }: TaxRatesTableProps): JSX.Element {
  const { form } = useScenarioForm();

  return (
    <form.Subscribe
      selector={(state) => state.values.areas[areaIndex]?.taxEntries ?? []}
    >
      {(entries) => (
        <div className={styles.wrapper}>
          <h4 className={styles.title}>Tax Rates</h4>
          <p className={styles.subtitle}>
            Each entry specifies a tax type, basis, and rate value for this
            area.
          </p>

          {entries.map((entry, entryIndex) => (
            <TaxRateRow
              key={entry.taxType}
              areaIndex={areaIndex}
              entryIndex={entryIndex}
              entry={entry}
              showLabels={entryIndex === 0}
            />
          ))}
        </div>
      )}
    </form.Subscribe>
  );
}

interface TaxRateRowProps {
  areaIndex: number;
  entryIndex: number;
  entry: TaxEntry;
  showLabels: boolean;
}

function TaxRateRow({
  areaIndex,
  entryIndex,
  entry,
  showLabels,
}: TaxRateRowProps): JSX.Element {
  const { form } = useScenarioForm();
  const prefix = `areas[${areaIndex}].taxEntries[${entryIndex}]` as const;
  const canVariable = VARIABLE_ELIGIBLE.includes(entry.taxType);
  const basisOptions = canVariable ? BASIS_OPTIONS : STANDARD_BASIS;
  const isVariable = entry.basis === 'variable';

  const handleBasisChange = (newBasis: TaxBasis) => {
    form.setFieldValue(`${prefix}.basis`, newBasis);
    if (newBasis === 'percentage') {
      form.setFieldValue(`${prefix}.value`, { rate: '' });
    } else if (newBasis === 'per_well') {
      form.setFieldValue(`${prefix}.value`, { amount: '' });
    } else if (newBasis === 'variable') {
      form.setFieldValue(`${prefix}.value`, {
        tiers: [
          { clientId: assignClientId(), maxPrice: 30, rate: '' },
          { clientId: assignClientId(), maxPrice: 60, rate: '' },
          { clientId: assignClientId(), maxPrice: null, rate: '' },
        ],
      });
    }
  };

  const rowContent = (
    <div className={styles.row}>
      <div className={styles.cellType}>
        <TextInput
          id={`type-${areaIndex}-${entryIndex}`}
          labelText={showLabels ? 'Tax Type' : ''}
          hideLabel={!showLabels}
          size="sm"
          value={TAX_TYPE_LABELS[entry.taxType]}
          readOnly
        />
      </div>

      <div className={styles.cellBasis}>
        <Dropdown
          id={`basis-${areaIndex}-${entryIndex}`}
          label="Select"
          titleText={showLabels ? 'Basis Selection' : ''}
          hideLabel={!showLabels}
          size="sm"
          items={basisOptions}
          selectedItem={basisOptions.find((b) => b.id === entry.basis) ?? null}
          itemToString={(item) => item?.label ?? ''}
          onChange={({
            selectedItem,
          }: {
            selectedItem: { id: TaxBasis; label: string } | null;
          }) => {
            if (selectedItem) handleBasisChange(selectedItem.id);
          }}
        />
      </div>

      {!isVariable && (
        <div className={styles.cellRate}>
          <TextInput
            id={`rate-${areaIndex}-${entryIndex}`}
            labelText={
              showLabels
                ? entry.basis === 'per_well'
                  ? 'Rate ($/Well)'
                  : 'Rate (%)'
                : ''
            }
            hideLabel={!showLabels}
            size="sm"
            type="number"
            min={entry.basis === 'per_well' ? undefined : '0'}
            max={entry.basis === 'per_well' ? undefined : '100'}
            step={entry.basis === 'per_well' ? '1' : '0.01'}
            placeholder={entry.basis === 'per_well' ? 'Enter Dollars' : '—'}
            value={
              entry.basis === 'per_well'
                ? entry.value.amount !== undefined && entry.value.amount !== ''
                  ? String(entry.value.amount)
                  : ''
                : entry.value.rate !== undefined && entry.value.rate !== ''
                  ? String(entry.value.rate)
                  : ''
            }
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const raw = e.target.value;
              const n = parseFloat(raw);
              if (entry.basis === 'per_well') {
                form.setFieldValue(`${prefix}.value`, {
                  amount: isNaN(n) ? '' : n,
                });
              } else {
                form.setFieldValue(`${prefix}.value`, {
                  rate: isNaN(n) ? '' : n,
                });
              }
            }}
          />
        </div>
      )}

      <div className={isVariable ? styles.cellNotesWide : styles.cellNotes}>
        <TextInput
          id={`notes-${areaIndex}-${entryIndex}`}
          labelText={showLabels ? 'Notes' : ''}
          hideLabel={!showLabels}
          size="sm"
          placeholder={TAX_TYPE_NOTES[entry.taxType]}
          value={entry.notes}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            form.setFieldValue(`${prefix}.notes`, e.target.value)
          }
        />
      </div>
    </div>
  );

  if (isVariable) {
    return (
      <div className={styles.variableBlock}>
        {rowContent}
        <VariableTierEditor
          areaIndex={areaIndex}
          entryIndex={entryIndex}
          tiers={entry.value.tiers ?? []}
          taxType={entry.taxType}
        />
      </div>
    );
  }

  return <div className={styles.dividerRow}>{rowContent}</div>;
}

// ─────────────────────────────────────
// Variable tier editor
// ─────────────────────────────────────

interface VariableTierEditorProps {
  areaIndex: number;
  entryIndex: number;
  tiers: PriceTier[];
  taxType: TaxType;
}

function VariableTierEditor({
  areaIndex,
  entryIndex,
  tiers,
  taxType,
}: VariableTierEditorProps): JSX.Element {
  const { form } = useScenarioForm();
  const priceUnit = TAX_TYPE_PRICE_UNIT[taxType] ?? '$/BBL';

  const updateTier = (tierIndex: number, patch: Partial<PriceTier>) => {
    const updated = tiers.map((t, i) => {
      return i === tierIndex ? { ...t, ...patch } : t;
    });
    form.setFieldValue(`areas[${areaIndex}].taxEntries[${entryIndex}].value`, {
      tiers: updated,
    });
  };

  const removeTier = (tierIndex: number) => {
    const updated = tiers.filter((_, i) => i !== tierIndex);
    form.setFieldValue(`areas[${areaIndex}].taxEntries[${entryIndex}].value`, {
      tiers: updated,
    });
  };

  const addTier = () => {
    const lastIdx = tiers.length - 1;
    const prevMax = lastIdx > 0 ? (tiers[lastIdx - 1].maxPrice ?? 0) : 0;
    const newTier: PriceTier = {
      clientId: assignClientId(),
      maxPrice: prevMax + 20,
      rate: '',
    };
    const updated = [...tiers];
    updated.splice(lastIdx, 0, newTier);
    form.setFieldValue(`areas[${areaIndex}].taxEntries[${entryIndex}].value`, {
      tiers: updated,
    });
  };

  return (
    <div className={styles.tierSection}>
      <div className={styles.tierHeader}>
        <span>Price up to ({priceUnit})</span>
        <span>Rate</span>
        <span />
      </div>

      {tiers.map((tier, tierIndex) => {
        const isLast = tierIndex === tiers.length - 1;
        return (
          <div key={tier.clientId ?? tierIndex} className={styles.tierRow}>
            <div className={styles.tierPrice}>
              {isLast ? (
                <span className={styles.noLimit}>No limit</span>
              ) : (
                <TextInput
                  id={`tier-price-${areaIndex}-${entryIndex}-${tierIndex}`}
                  labelText=""
                  hideLabel
                  size="sm"
                  placeholder="30"
                  value={
                    tier.maxPrice !== null && tier.maxPrice !== undefined
                      ? String(tier.maxPrice)
                      : ''
                  }
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const n = parseFloat(e.target.value);
                    updateTier(tierIndex, {
                      maxPrice: isNaN(n) ? null : n,
                    });
                  }}
                />
              )}
            </div>
            <div className={styles.tierRate}>
              <TextInput
                id={`tier-rate-${areaIndex}-${entryIndex}-${tierIndex}`}
                labelText=""
                hideLabel
                size="sm"
                type="number"
                min="0"
                max="100"
                step="0.01"
                placeholder="—"
                value={tier.rate !== '' ? String(tier.rate) : ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const n = parseFloat(e.target.value);
                  updateTier(tierIndex, {
                    rate: isNaN(n) ? '' : n,
                  });
                }}
              />
            </div>
            {!isLast && (
              <Button
                kind="danger--ghost"
                size="sm"
                hasIconOnly
                renderIcon={TrashCan}
                iconDescription="Remove tier"
                onClick={() => removeTier(tierIndex)}
              />
            )}
          </div>
        );
      })}

      <Button kind="ghost" size="sm" renderIcon={Add} onClick={addTier}>
        Add Tier
      </Button>
    </div>
  );
}
